import { ref, computed, watch, getCurrentInstance } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useToast } from '@/composables/common/useToast';
import { useQueryList as useListQuery } from '@/composables/common/useQueryList';

export function useFrecuenciasIndex() {
    const toast = useToast();
    const vm = getCurrentInstance();

    const list = useListQuery({
        fetcher: async params => {
            const res = await store.dispatch(
                config.getVuexAlias('back.frecuencias.index'),
                params
            );
            return res.data || { data: [], meta: {} };
        },
        listaIdSource: 'store',
        getListaId: () => {
            // En bases: el lista_id viene de la URL (reactivo via vm.proxy.$route)
            // En abonos: cae al store (lista activa)
            const $route = vm && vm.proxy && vm.proxy.$route;
            if ($route && $route.params && $route.params.lista_id) {
                return $route.params.lista_id;
            }
            return store.getters.getListaId;
        },
    });

    const listaId = list.listaId;

    // Refetch when the season selector changes :lista_id in the URL.
    // Mirrors the pattern in useBasesIndex (which uses `watchListaId: true`
    // + `listaIdSource: 'route'`). This composable uses `'store'` so
    // useQueryList's built-in watchListaId watcher is never created (route
    // is null for that source), and we need this explicit watcher.
    watch(() => vm.proxy.$route.params.lista_id, (newId, oldId) => {
        if (newId && newId !== oldId) {
            list.fetch();
        }
    });

    // --- Modal state ---

    const editFrecuenciaData = ref(null);
    const deleteFrecuenciaId = ref(null);

    function showEditModal(data) {
        editFrecuenciaData.value = data;
    }

    function closeEditModal() {
        editFrecuenciaData.value = null;
    }

    function showDeleteModal(id) {
        deleteFrecuenciaId.value = id;
    }

    function closeDeleteModal() {
        deleteFrecuenciaId.value = null;
    }

    function closeCreateModal() {}

    // --- Columns ---

    const frecuenciasColumns = [
        { label: 'Cantidad', field: 'cantidad' },
        { label: 'Frecuencia', field: 'frecuencia' },
        { label: 'Acciones', field: 'acciones' }
    ];

    // --- Data fetching (fallback for manual refresh) ---

    async function fetch() {
        return list.fetch();
    }

    // --- Delete ---

    async function borrar(frecuenciaId) {
        const params = {
            lista_id: listaId.value,
            frecuencia_id: frecuenciaId,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.frecuencias.borrar'),
                params
            );
            toast.success(res.data.message || 'Frecuencia eliminada correctamente');
            fetch();
            closeDeleteModal();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar la frecuencia');
        }
    }

    // --- Create ---

    async function crear(formData) {
        const params = {
            lista_id: listaId.value,
            cantidad: formData.cantidad,
            frecuencia: formData.frecuencia,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.frecuencias.crear'),
                params
            );
            toast.success(res.data.message || 'Frecuencia creada correctamente');
            fetch();
            closeCreateModal();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al crear la frecuencia');
        }
    }

    // --- Edit ---

    async function editar(formData) {
        const params = {
            lista_id: listaId.value,
            frecuencia_id: formData.id,
            cantidad: formData.cantidad,
            frecuencia: formData.frecuencia,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.frecuencias.editar'),
                params
            );
            toast.success(res.data.message || 'Frecuencia actualizada correctamente');
            fetch();
            closeEditModal();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al actualizar la frecuencia');
        }
    }

    // --- Save (legacy alias) ---

    async function save(formData) {
        if (formData.id) {
            await editar(formData);
        } else {
            await crear(formData);
        }
    }

    return {
        frecuencias: list.data,
        loading: list.loading,
        filters: list.filters,
        meta: list.meta,
        listaId,
        frecuenciasColumns,
        fetch,
        borrar,
        crear,
        editar,
        save,
        editFrecuenciaData,
        closeEditModal,
        showEditModal,
        deleteFrecuenciaId,
        closeDeleteModal,
        showDeleteModal,
        closeCreateModal,
    };
}
